/**
 * Виджет reCAPTCHA v2 «я не робот».
 *
 * Скрипт Google подключается только когда капча настроена на сервере: без
 * ключей форма не должна тянуть чужой код и давать Google видеть посетителей.
 */
import { useEffect, useRef } from "react";

interface Props {
  siteKey: string;
  onToken: (token: string | null) => void;
}

// Типы глобального объекта, который добавляет скрипт Google.
interface ReCaptcha {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      theme?: "dark" | "light";
      callback: (token: string) => void;
      "expired-callback": () => void;
      "error-callback": () => void;
    },
  ) => number;
  reset: (widgetId?: number) => void;
  ready: (callback: () => void) => void;
}

declare global {
  interface Window {
    grecaptcha?: ReCaptcha;
  }
}

const SCRIPT_ID = "recaptcha-script";
// hl=ru: без него виджет говорит по-английски посреди русского интерфейса.
const SCRIPT_SRC = "https://www.google.com/recaptcha/api.js?render=explicit&hl=ru";

/** Загружает скрипт один раз на всё приложение. */
function loadScript(): Promise<void> {
  if (window.grecaptcha) return Promise.resolve();

  const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
  if (existing) {
    return new Promise((resolve) => existing.addEventListener("load", () => resolve()));
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("не удалось загрузить reCAPTCHA"));
    document.head.appendChild(script);
  });
}

export function Captcha({ siteKey, onToken }: Props) {
  const container = useRef<HTMLDivElement>(null);
  const widgetId = useRef<number | null>(null);

  // Колбэк в ref: смена обработчика не должна перерисовывать виджет,
  // иначе пользователю пришлось бы проходить проверку заново.
  const handler = useRef(onToken);
  useEffect(() => {
    handler.current = onToken;
  }, [onToken]);

  useEffect(() => {
    if (!siteKey) return;
    let disposed = false;

    void loadScript()
      .then(() => {
        if (disposed || !container.current || widgetId.current !== null) return;

        window.grecaptcha?.ready(() => {
          if (disposed || !container.current || widgetId.current !== null) return;

          const id = window.grecaptcha?.render(container.current, {
            sitekey: siteKey,
            theme: "dark",
            callback: (token) => handler.current(token),
            // Токен живёт пару минут: после истечения проверку нужно
            // проходить заново, иначе сервер отклонит форму.
            "expired-callback": () => handler.current(null),
            "error-callback": () => handler.current(null),
          });
          widgetId.current = id ?? null;
        });
      })
      .catch(() => handler.current(null));

    return () => {
      disposed = true;
    };
  }, [siteKey]);

  if (!siteKey) return null;

  return <div ref={container} className="flex justify-center" />;
}
